/**
 * Money Formatters — Canonical helpers for money display and normalization.
 *
 * Consolidates DUP-1 (formatMoneyDisplay 3×) and DUP-2 (normalizeCents 2×).
 * Single source of truth — all money formatting imports from here.
 *
 * Created: Stability Audit 2026, Ronde 2 (DUP-1+2).
 */

/** Type guard: true when `n` is a finite number (not NaN, not ±Infinity). */
export function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n)
}

/**
 * Normalize a value to a non-negative integer cent amount.
 * Returns `null` when the input is not a finite number.
 */
export function normalizeCents(n: unknown): number | null {
  if (!isFiniteNumber(n)) return null
  return Math.max(0, Math.round(n))
}

/**
 * Format a cent amount as a Dutch-style euro string.
 *
 * Examples:
 *   formatMoneyDisplay(350)  → "€3,50"
 *   formatMoneyDisplay(500)  → "€5,-"
 *   formatMoneyDisplay(-10)  → "€0,-"
 */
export function formatMoneyDisplay(cents: number): string {
  const safe = Math.max(0, Math.round(cents))
  const euros = Math.floor(safe / 100)
  const remaining = safe % 100
  if (remaining === 0) return `€${euros},-`
  return `€${euros},${remaining.toString().padStart(2, '0')}`
}
